'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface Country {
  id: number;
  name: string;
  code: string;
  capital: string;
  activeRoutes: number;
  offices: number;
  coordinates: { lat: number; lng: number };
  color: string;
}

const RegionalCoverage = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const countries: Country[] = [
    {
      id: 1,
      name: "Democratic Republic of Congo",
      code: "DRC",
      capital: "Kinshasa",
      activeRoutes: 47,
      offices: 3,
      coordinates: { lat: -4.0383, lng: 21.7587 },
      color: "from-blue-400 to-blue-600"
    },
    {
      id: 2,
      name: "Kenya",
      code: "KE",
      capital: "Nairobi",
      activeRoutes: 38,
      offices: 2,
      coordinates: { lat: -1.2864, lng: 36.8172 },
      color: "from-emerald-400 to-emerald-600"
    },
    {
      id: 3,
      name: "Tanzania",
      code: "TZ",
      capital: "Dodoma",
      activeRoutes: 42,
      offices: 2,
      coordinates: { lat: -6.1630, lng: 35.7516 },
      color: "from-amber-400 to-amber-600"
    },
    {
      id: 4,
      name: "Uganda",
      code: "UG",
      capital: "Kampala",
      activeRoutes: 31,
      offices: 1,
      coordinates: { lat: 0.3476, lng: 32.5825 },
      color: "from-purple-400 to-purple-600"
    },
    {
      id: 5,
      name: "Rwanda",
      code: "RW",
      capital: "Kigali",
      activeRoutes: 24,
      offices: 1,
      coordinates: { lat: -1.9403, lng: 29.8739 },
      color: "from-rose-400 to-rose-600"
    },
    {
      id: 6,
      name: "Burundi",
      code: "BI",
      capital: "Gitega",
      activeRoutes: 18,
      offices: 1,
      coordinates: { lat: -3.3731, lng: 29.9189 },
      color: "from-cyan-400 to-cyan-600"
    }
  ];

  const handleCountrySelect = (countryId: number) => {
    if (!isHydrated) return;
    setSelectedCountry(selectedCountry === countryId ? null : countryId);
  };

  const totalRoutes = countries.reduce((sum, country) => sum + country.activeRoutes, 0);
  const totalOffices = countries.reduce((sum, country) => sum + country.offices, 0);

  return (
    <section className="py-16 bg-muted">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-golden-light/10 via-golden-mid/10 to-golden-dark/10 rounded-full mb-4">
            <Icon name="MapPinIcon" size={20} className="text-golden-mid" />
            <span className="font-cta font-semibold text-sm text-primary">Regional Network</span>
          </div>
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
            Extensive African Coverage
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
            Operating across six countries with {totalRoutes} active routes and {totalOffices} regional offices
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map Section */}
          <div className="lg:col-span-2">
            <div className="bg-card rounded-xl p-6 shadow-md border border-border">
              <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-100">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title="MylesDrive Regional Coverage Map"
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps?q=-2.5,30.5&z=5&output=embed"
                  className="border-0"
                />
              </div>
              
              <div className="mt-6 grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="font-headline font-bold text-2xl text-foreground mb-1">
                    {totalRoutes}
                  </div>
                  <div className="font-body text-sm text-muted-foreground">Active Routes</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="font-headline font-bold text-2xl text-foreground mb-1">
                    {countries.length}
                  </div>
                  <div className="font-body text-sm text-muted-foreground">Countries</div>
                </div>
                <div className="text-center p-4 bg-muted rounded-lg">
                  <div className="font-headline font-bold text-2xl text-foreground mb-1">
                    {totalOffices}
                  </div>
                  <div className="font-body text-sm text-muted-foreground">Regional Offices</div>
                </div>
              </div>
            </div>
          </div>

          {/* Countries List */}
          <div className="space-y-3">
            {countries.map((country) => (
              <div
                key={country.id}
                className={`bg-card rounded-lg p-4 shadow-sm border transition-all duration-300 cursor-pointer ${
                  selectedCountry === country.id
                    ? 'border-golden-mid shadow-golden-md'
                    : 'border-border hover:border-golden-light'
                }`}
                onClick={() => handleCountrySelect(country.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <div className={`w-3 h-3 rounded-full bg-gradient-to-br ${country.color}`} />
                      <h3 className="font-headline font-bold text-base text-foreground">
                        {country.name}
                      </h3>
                    </div>
                    <p className="font-body text-sm text-muted-foreground">
                      Capital: {country.capital}
                    </p>
                  </div>
                  <Icon
                    name={selectedCountry === country.id ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                    size={20}
                    className="text-muted-foreground"
                  />
                </div>

                {isHydrated && selectedCountry === country.id && (
                  <div className="mt-3 pt-3 border-t border-border space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Active Routes</span>
                      <span className="font-mono font-semibold text-sm text-foreground">
                        {country.activeRoutes}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-body text-sm text-muted-foreground">Offices</span>
                      <span className="font-mono font-semibold text-sm text-foreground">
                        {country.offices}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link
            href="/regional-coverage"
            className="inline-flex items-center space-x-2 px-8 py-4 font-cta font-semibold text-base text-primary bg-gradient-to-r from-golden-light via-golden-mid to-golden-dark rounded-lg shadow-golden-md hover:shadow-golden-lg transition-all duration-300 hover:-translate-y-1"
          >
            <span>Explore Full Coverage</span>
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RegionalCoverage;